import { useLayoutEffect, useRef } from 'react';
import { nativeCall, type NativeCallState } from './nativeBridge';
import { acknowledgeNativeViewport, currentViewportRevision, nextViewportRevision, onNativeViewportRefresh, type NativeViewportReceipt } from './nativeVideoViewport';

/** This rectangle is occupied by native SDK video views, never a browser media stream. */
export function NativeVideoStage({ state, visible, viewMode, pinnedId, onError, onViewportAcknowledged }: {
  state: NativeCallState; visible: boolean; viewMode: 'speaker' | 'grid'; pinnedId?: string; onError: (message: string) => void; onViewportAcknowledged?: (receipt: NativeViewportReceipt) => void;
}) {
  const element = useRef<HTMLDivElement>(null);
  const { meetingId, generation } = state;
  useLayoutEffect(() => {
    if (!meetingId || generation === undefined) return;
    let frame = 0;
    let disposed = false;
    const update = () => {
      const revision = nextViewportRevision();
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        if (disposed) return;
        const rect = element.current?.getBoundingClientRect();
        if (!rect) return;
        const shown = visible && document.visibilityState === 'visible' && rect.width > 0 && rect.height > 0 && [rect.x, rect.y, rect.width, rect.height].every(Number.isFinite);
        void nativeCall.viewport({ meetingId, generation }, { x: rect.x, y: rect.y, width: rect.width, height: rect.height, visible: shown, viewMode, pinnedId })
          .then(() => {
            if (disposed || revision !== currentViewportRevision() || !shown || document.visibilityState !== 'visible') return;
            const receipt = { meetingId, generation, revision };
            onViewportAcknowledged?.(receipt);
            acknowledgeNativeViewport(receipt);
          })
          .catch(async (error: unknown) => {
            if (disposed || revision !== currentViewportRevision()) return;
            if (typeof error === 'object' && error !== null && 'code' in error && error.code === 'STALE_CALL') {
              try {
                // Native room changes can retire this scope before React receives
                // its next snapshot. STALE_CALL alone can also mean a missing view.
                const current = await nativeCall.getState();
                if (current && typeof current.meetingId === 'string' && current.meetingId.trim().length > 0
                  && typeof current.generation === 'number' && Number.isSafeInteger(current.generation) && current.generation >= 0
                  && ['idle', 'preview', 'joining', 'joined', 'waiting', 'denied', 'left', 'error'].includes(current.state)
                  && (current.meetingId !== meetingId || current.generation !== generation)) return;
              } catch {
                // An unreadable scope does not establish that this failure is stale.
              }
            }
            if (!disposed && revision === currentViewportRevision()) onError('Unable to display the call. Close and reopen the call view.');
          });
      });
    };
    const observer = typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(update);
    if (element.current) observer?.observe(element.current);
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    document.addEventListener('visibilitychange', update);
    window.visualViewport?.addEventListener('resize', update);
    window.visualViewport?.addEventListener('scroll', update);
    const stopRefresh = onNativeViewportRefresh(update);
    update();
    return () => {
      disposed = true; nextViewportRevision(); stopRefresh(); cancelAnimationFrame(frame); observer?.disconnect();
      window.removeEventListener('resize', update); window.removeEventListener('scroll', update, true);
      document.removeEventListener('visibilitychange', update);
      window.visualViewport?.removeEventListener('resize', update); window.visualViewport?.removeEventListener('scroll', update);
      void nativeCall.viewport({ meetingId, generation }, { x: 0, y: 0, width: 0, height: 0, visible: false, viewMode }).catch(() => undefined);
    };
  }, [meetingId, generation, visible, viewMode, pinnedId, onError, onViewportAcknowledged]);
  return <div ref={element} data-testid="native-video-stage" aria-label="Meeting video" className="min-h-0 flex-1 bg-[#0f1419]" />;
}
